import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { useThirdPartyToolsSetup } from '@/utils/composables/useThirdPartyToolsSetup';
import { useWebPush } from '@/utils/composables/useWebPush';
import {
  useGroups,
  usePersonalSurveyProfile,
  useTeamInfo,
  useTeamUsers,
  useThemeList,
} from '@/utils/swr';
import { useUserSession } from '@/utils/user';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';
import { featureFlags } from '@/featureFlags';

export const useAppSetup = () => {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const feedType = computed(() => store.state.feedType.feedType);
  const { userInfo } = useUserSession();
  const { data: teamInfo } = useTeamInfo();
  const { data: groups, mutate: mutateGroups } = useGroups();
  const { data: themes, mutate: mutateThemes } = useThemeList(feedType);
  const { data: teamUsers } = useTeamUsers();
  const enablePersonalHint = computed(() => {
    return !!teamInfo.value?.enable_personal_survey_mail;
  });
  const { data: personalSurveyProfile } =
    usePersonalSurveyProfile(enablePersonalHint);

  useThirdPartyToolsSetup({
    userInfo,
    teamInfo,
    personalSurveyProfile,
    optionalIntegrations: {
      zendesk: !featureFlags.ANDEV_9844_HELP_MENU_RENEWAL,
    },
  });

  const stopInitialDataWatch = watch(
    [teamInfo, groups, themes, teamUsers],
    ([t, gs, ts, tus]) => {
      if (!t || !gs || !ts || !tus) return;

      store.commit('teamInfo/setTeamInfo', t);
      store.commit('groups/setGroups', gs.groups);
      store.commit('themeList/setThemes', ts.themes);
      store.commit('teamUsers/setTeamUsers', tus.users);

      const gotFeedType = localStorage.getItem('UserSession.FeedType');
      if (gotFeedType === null) {
        const feedType = t.enable_foreign_lang_media ? 'all' : 'domestic';
        userSession.setFeedType(feedType);
        store.commit('feedType/setFeedType', feedType);
      }
      stopInitialDataWatch();
    },
  );

  // グループの参加・退会でグループテーマの参加・お気に入りも変わるため、テーマ一覧も取り直す。
  // グループメニューは表示中しかマウントされないので、常にマウントされているここで受け取る。
  const emitter = useEmitter();
  const refreshGroupsAndThemes = async () => {
    await Promise.all([mutateGroups(), mutateThemes()]);
    if (groups.value) store.commit('groups/setGroups', groups.value.groups);
    if (themes.value) store.commit('themeList/setThemes', themes.value.themes);
  };
  emitter.on('update-group-list', refreshGroupsAndThemes);
  onBeforeUnmount(() =>
    emitter.off('update-group-list', refreshGroupsAndThemes),
  );

  const hasCommentQueryParams = computed(() => {
    const query = route.query;
    return query && query.widget === 'comment' && query.articleId;
  });

  const fetchArticle = async () => {
    const article = await api.fetchDocument(
      Number(route.query.articleId),
      'article',
    );
    return article;
  };

  const redirectToCommentPage = async (): Promise<void> => {
    const article = await fetchArticle();
    if (article.comments?.length > 0) {
      router.replace(`/comments/${article.comments[0].id}`);
    } else {
      router.replace('/');
    }
  };

  const stopUserInfoWatch = watch(userInfo, async u => {
    if (!u) return;

    if (hasCommentQueryParams.value) {
      redirectToCommentPage();
    }
    await api.trackAccess();
    stopUserInfoWatch();
  });

  watch(
    () => route.query,
    () => {
      if (hasCommentQueryParams.value) {
        redirectToCommentPage();
      }
    },
  );

  watch(teamInfo, () => {
    if (
      teamInfo.value?.enable_foreign_lang_media === false &&
      feedType.value !== 'domestic'
    ) {
      store.dispatch('feedType/updateFeedType', 'domestic');
    }
  });

  /**
   * 検索履歴を使用して検索したかどうかのフラグ
   * NOTE: `keyword-search-bar.vue`と`search-results.vue`
   * の親子関係にないコンポーネント間で値を共有するためにprovideをここで定義
   */
  const useSearchHistory = ref(false);
  provide('useSearchHistory', useSearchHistory);

  const { createSnackbar } = useSnackbar();
  const { initWebPushWithNotification } = useWebPush();

  onMounted(() => {
    store.dispatch('searchResults/resetSearchCondition');

    document.addEventListener('visibilitychange', () => {
      if (!userSession.isActive()) return;
      if (document.visibilityState === 'visible') {
        api.trackAccess();
      }
    });

    initWebPushWithNotification(createSnackbar);
  });
};
